import { type FC, type ReactNode } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useDroppable } from '@dnd-kit/core';
import type { Task } from '../types';
import { useTheme } from '../hooks/useTheme';
import TaskCard from './TaskCard';

interface TaskColumnProps {
  status: 'pending' | 'in-progress' | 'completed';
  title: ReactNode;
  tasks: Task[];
  onDrop?: (taskId: string, newStatus: 'pending' | 'in-progress' | 'completed') => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onArchive?: (taskId: string) => void;
}

const TaskColumn: FC<TaskColumnProps> = ({ status, title, tasks, onEdit, onDelete, onArchive }) => {
  const { theme } = useTheme();
  
  // Setup droppable area
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const columnColors = {
    pending: 'border-neon-yellow',
    'in-progress': 'dark:border-white border-black', // Use theme color (white/black) for in-progress
    completed: 'border-neon-green',
  };

  return (
    <div
      ref={setNodeRef}
      className={`
        flex-1
        min-w-full sm:min-w-[280px] md:min-w-[300px]
        p-3 sm:p-4
        rounded-lg
        border-2
        ${columnColors[status]}
        dark:bg-dark-surface
        bg-light-surface
        transition-all
        ${isOver ? 'shadow-neon scale-105' : 'shadow-md'}
      `}
    >
      {/* Column header */}
      <div className="mb-3 sm:mb-4">
        <h3 className="
          text-base sm:text-lg font-bold
          mb-1
        "
        style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}
        >
          {title}
        </h3>
        <p className="text-xs sm:text-sm" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
          {tasks.length} task{tasks.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Tasks */}
      <div className="space-y-2 sm:space-y-3 min-h-[150px] sm:min-h-[200px]">
        <AnimatePresence mode="popLayout">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onEdit={onEdit}
              onDelete={onDelete}
              onArchive={onArchive}
            />
          ))}
        </AnimatePresence>
        
        {tasks.length === 0 && (
          <div 
            className="
              flex items-center justify-center
              h-24 sm:h-32
              border-2 border-dashed
              dark:border-dark-hover
              border-gray-300
              rounded-lg
              text-xs sm:text-sm
            "
            style={{ color: theme === 'dark' ? '#6b7280' : '#9ca3af' }}
          >
            {isOver ? 'Drop here' : 'No tasks'}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskColumn;
